const ICONS = {
  Mail: require("@material-symbols/svg-400/outlined/mail-fill.svg"),
  PersonRemove: require("@material-symbols/svg-400/outlined/person_remove-fill.svg"),
  Schedule: require("@material-symbols/svg-400/outlined/schedule-fill.svg"),
  CheckCircle: require("@material-symbols/svg-400/outlined/check_circle-fill.svg"),
  ShoppingCart: require("@material-symbols/svg-400/outlined/shopping_cart-fill.svg"),
  AttachMoney: require("@material-symbols/svg-400/outlined/attach_money-fill.svg"),
  LocalShipping: require("@material-symbols/svg-400/outlined/local_shipping-fill.svg"),
  Inventory: require("@material-symbols/svg-400/outlined/inventory_2-fill.svg"),
};

const FOOTER_LABELS = {
  newDealer: "dealershipApplications.analytics.footer.newDealer",
  addedToExisting: "dealershipApplications.analytics.footer.addedToExisting",
  newDealers: "dealershipApplications.analytics.footer.newDealers",
  addedToExistingOnes: "dealershipApplications.analytics.footer.addedToExistingOnes",
};

import MoneyServiceDefault, { DOUBLE_ZERO } from "@/services/_bit/MoneyService";

const moneyService = new MoneyServiceDefault();

const formatCountWithPercent = (count, percent) => `${count ?? 0} (${percent ?? 0}%)`;
const formatMoney = (amount) => `$${amount ?? 0}`;
const formatMoneyWithPercent = (amount, percent) => `$${amount ?? 0} (${percent ?? 0}%)`;

const formatSeparatedValue = (value, decimalPlaces) => {
  const { integer, penny, delimiter } = moneyService.separatedMoney(value ?? 0, decimalPlaces);

  if (decimalPlaces === 0) {
    return integer;
  }

  return penny === DOUBLE_ZERO ? integer : `${integer}${delimiter}${penny}`;
};

export const formatAnalyticsCount = (value) => formatSeparatedValue(value, 0);

export const formatAnalyticsMoney = (amount) => `$${formatSeparatedValue(amount, 2)}`;

export const formatAnalyticsPercent = (percent) => `(${percent ?? 0}%)`;

export const formatCountMetricCell = (data = {}) => ({
  primary: formatAnalyticsCount(data.total),
  secondary: formatAnalyticsPercent(data.percent),
  footerLeft: formatAnalyticsCount(data.newDealer),
  footerRight: formatAnalyticsCount(data.addedToExisting),
});

export const formatSumMetricCell = (data = {}) => ({
  primary: formatAnalyticsMoney(data.sum),
  secondary: formatAnalyticsPercent(data.percent),
  footerLeft: formatAnalyticsMoney(data.newDealer),
  footerRight: formatAnalyticsMoney(data.addedToExisting),
});

export const formatOrdersSumCell = (data = {}) => ({
  primary: formatAnalyticsMoney(data.sum),
  secondary: data.sharePercent != null ? formatAnalyticsPercent(data.sharePercent) : null,
  footerLeft: formatAnalyticsMoney(data.newDealer?.sum),
  footerRight: formatAnalyticsMoney(data.addedToExisting?.sum),
});

const formatMetricWithFooter = (data) => ({
  value: String(data.total ?? 0),
  contentTextParams: { percent: String(data.percent ?? 0) },
  footer: {
    left: { value: formatCountWithPercent(data.newDealer, data.newDealerPercent) },
    right: { value: formatCountWithPercent(data.addedToExisting, data.addedToExistingPercent) },
  },
});

export const CARD_FORMATTERS = {
  received: (summary) => ({
    value: String(summary.received ?? 0),
  }),

  rejected: (summary) => ({
    value: String(summary.rejected ?? 0),
    contentTextParams: { percent: String(summary.rejectedPercent ?? 0) },
  }),

  inProgress: (summary) => ({
    value: String(summary.inProgress ?? 0),
    contentTextParams: { percent: String(summary.inProgressPercent ?? 0) },
  }),

  dealerCreated: (summary) => formatMetricWithFooter(summary.dealerCreated || {}),

  placedOrder: (summary) => formatMetricWithFooter(summary.madeOrder || {}),

  ordersAmount: (summary) => {
    const data = summary.ordersSum || {};
    const newDealer = data.newDealer || {};
    const addedToExisting = data.addedToExisting || {};

    return {
      value: formatMoney(data.sum),
      contentTextParams: { count: String(data.count ?? 0) },
      footer: {
        left: { value: formatMoneyWithPercent(newDealer.sum, data.newDealerPercent) },
        right: { value: formatMoneyWithPercent(addedToExisting.sum, data.addedToExistingPercent) },
      },
    };
  },

  shippedAmount: (summary) => {
    const data = summary.shippedSum || {};

    return {
      value: formatMoney(data.sum),
      contentTextParams: { percent: String(data.percent ?? 0) },
      footer: {
        left: { value: formatMoneyWithPercent(data.newDealer, data.newDealerPercent) },
        right: { value: formatMoneyWithPercent(data.addedToExisting, data.addedToExistingPercent) },
      },
    };
  },

  noOrders: (summary) => formatMetricWithFooter(summary.noOrders || {}),
};

export const MANAGER_ANALYTICS_FIELDS = [
  { key: "received", labelKey: "label.received", type: "count" },
  { key: "rejected", labelKey: "label.rejected", type: "count" },
  { key: "inProgress", labelKey: "label.inProgress", type: "count" },
  { key: "dealerCreated", labelKey: "label.dealerCreated", type: "countMetric" },
  { key: "madeOrder", labelKey: "label.madeOrder", type: "countMetric" },
  { key: "ordersSum", labelKey: "label.ordersSum", type: "ordersSum" },
  { key: "shippedSum", labelKey: "label.shippedSum", type: "sumMetric" },
  { key: "noOrders", labelKey: "label.noOrders", type: "countMetric" },
  {
    key: "averageProcessingTime",
    labelKey: "label.averageProcessingTime",
    type: "time",
  },
];

export const MANAGER_ANALYTICS_SORT_OPTIONS = [
  {
    id: 1,
    labelKey: "dealershipApplications.analytics.sort.managerNameAsc",
    sortable: "managerName",
  },
  {
    id: 2,
    labelKey: "dealershipApplications.analytics.sort.managerNameDesc",
    sortable: "-managerName",
  },
  { id: 3, labelKey: "dealershipApplications.analytics.sort.receivedDesc", sortable: "-received" },
  { id: 4, labelKey: "dealershipApplications.analytics.sort.receivedAsc", sortable: "received" },
  { id: 5, labelKey: "dealershipApplications.analytics.sort.rejectedDesc", sortable: "-rejected" },
  { id: 6, labelKey: "dealershipApplications.analytics.sort.rejectedAsc", sortable: "rejected" },
  {
    id: 7,
    labelKey: "dealershipApplications.analytics.sort.inProgressDesc",
    sortable: "-inProgress",
  },
  {
    id: 8,
    labelKey: "dealershipApplications.analytics.sort.inProgressAsc",
    sortable: "inProgress",
  },
  {
    id: 9,
    labelKey: "dealershipApplications.analytics.sort.dealerCreatedDesc",
    sortable: "-dealerCreated",
  },
  {
    id: 10,
    labelKey: "dealershipApplications.analytics.sort.dealerCreatedAsc",
    sortable: "dealerCreated",
  },
  {
    id: 11,
    labelKey: "dealershipApplications.analytics.sort.madeOrderDesc",
    sortable: "-madeOrder",
  },
  { id: 12, labelKey: "dealershipApplications.analytics.sort.madeOrderAsc", sortable: "madeOrder" },
  {
    id: 13,
    labelKey: "dealershipApplications.analytics.sort.ordersSumDesc",
    sortable: "-ordersSum",
  },
  { id: 14, labelKey: "dealershipApplications.analytics.sort.ordersSumAsc", sortable: "ordersSum" },
  {
    id: 15,
    labelKey: "dealershipApplications.analytics.sort.shippedSumDesc",
    sortable: "-shippedSum",
  },
  {
    id: 16,
    labelKey: "dealershipApplications.analytics.sort.shippedSumAsc",
    sortable: "shippedSum",
  },
  { id: 17, labelKey: "dealershipApplications.analytics.sort.noOrdersDesc", sortable: "-noOrders" },
  { id: 18, labelKey: "dealershipApplications.analytics.sort.noOrdersAsc", sortable: "noOrders" },
];

export const DEFAULT_MANAGER_ANALYTICS_SORT_OPTION_ID = 3;

export const ANALYTICS_CARDS_CONFIG = [
  {
    id: "received",
    titleKey: "dealershipApplications.analytics.cards.received.title",
    icon: ICONS.Mail,
    iconColor: "blue",
    value: "0",
    contentTextKey: "dealershipApplications.analytics.cards.received.contentText",
  },
  {
    id: "rejected",
    titleKey: "dealershipApplications.analytics.cards.rejected.title",
    icon: ICONS.PersonRemove,
    iconColor: "orange",
    value: "0",
    contentTextKey: "dealershipApplications.analytics.cards.rejected.contentText",
    contentTextParams: { percent: "0" },
  },
  {
    id: "inProgress",
    titleKey: "dealershipApplications.analytics.cards.inProgress.title",
    icon: ICONS.Schedule,
    iconColor: "blue",
    value: "0",
    contentTextKey: "dealershipApplications.analytics.cards.inProgress.contentText",
    contentTextParams: { percent: "0" },
  },
  {
    id: "dealerCreated",
    titleKey: "dealershipApplications.analytics.cards.dealerCreated.title",
    icon: ICONS.CheckCircle,
    iconColor: "green",
    value: "0",
    contentTextKey: "dealershipApplications.analytics.cards.dealerCreated.contentText",
    contentTextParams: { percent: "0" },
    footer: {
      left: {
        labelKey: FOOTER_LABELS.newDealer,
        value: "0 (0%)",
      },
      right: {
        labelKey: FOOTER_LABELS.addedToExisting,
        value: "0 (0%)",
      },
    },
  },
  {
    id: "placedOrder",
    titleKey: "dealershipApplications.analytics.cards.placedOrder.title",
    icon: ICONS.ShoppingCart,
    iconColor: "green",
    value: "0",
    contentTextKey: "dealershipApplications.analytics.cards.placedOrder.contentText",
    contentTextParams: { percent: "0" },
    footer: {
      left: {
        labelKey: FOOTER_LABELS.newDealers,
        value: "0 (0%)",
      },
      right: {
        labelKey: FOOTER_LABELS.addedToExistingOnes,
        value: "0 (0%)",
      },
    },
  },
  {
    id: "ordersAmount",
    titleKey: "dealershipApplications.analytics.cards.ordersAmount.title",
    icon: ICONS.AttachMoney,
    iconColor: "blue",
    value: "$0",
    contentTextKey: "dealershipApplications.analytics.cards.ordersAmount.contentText",
    contentTextParams: { count: "0" },
    footer: {
      left: {
        labelKey: FOOTER_LABELS.newDealers,
        value: "$0 (0%)",
      },
      right: {
        labelKey: FOOTER_LABELS.addedToExistingOnes,
        value: "$0 (0%)",
      },
    },
  },
  {
    id: "shippedAmount",
    titleKey: "dealershipApplications.analytics.cards.shippedAmount.title",
    icon: ICONS.LocalShipping,
    iconColor: "blue",
    value: "$0",
    contentTextKey: "dealershipApplications.analytics.cards.shippedAmount.contentText",
    contentTextParams: { percent: "0" },
    footer: {
      left: {
        labelKey: FOOTER_LABELS.newDealers,
        value: "$0 (0%)",
      },
      right: {
        labelKey: FOOTER_LABELS.addedToExistingOnes,
        value: "$0 (0%)",
      },
    },
  },
  {
    id: "noOrders",
    titleKey: "dealershipApplications.analytics.cards.noOrders.title",
    icon: ICONS.Inventory,
    iconColor: "orange",
    value: "0",
    contentTextKey: "dealershipApplications.analytics.cards.noOrders.contentText",
    contentTextParams: { percent: "0" },
    footer: {
      left: {
        labelKey: FOOTER_LABELS.newDealers,
        value: "0 (0%)",
      },
      right: {
        labelKey: FOOTER_LABELS.addedToExistingOnes,
        value: "0 (0%)",
      },
    },
  },
];
